import type { AriaSortableOptions } from 'go-modules/aria-decorator/aria-ng-sortable.decorator';
import { ResponsiveViewService, Sizes } from 'go-modules/responsive-view/responsive-view.service';
import * as Sortable from 'sortablejs';
import elements from './elements.json';

export interface Bindings {
	rubricController: any;
}

export class RubricWidgetPanelController implements Bindings {

	// Bindings
	public rubricController: any;

	public elements: any[];
	public sortableOptions: AriaSortableOptions;

	private isDragging = false;
	private suppressClick = false;

	/* @ngInject */
	constructor (
		private $scope: ng.IScope,
		private $translate,
		private responsiveView: ResponsiveViewService,
		private $timeout: ng.ITimeoutService,
		private $document: ng.IDocumentService
	) {}

	public $onInit (): void {
		this.elements = Array.from(elements);
		this.sortableOptions = {
			group: {
				name: 'rubric-group',
				pull: 'clone',
				put: false
			},
			putSpeakerAdditionalText: {
				prepend: this.$translate.instant('rubric-element_rubric-element-added')
			},
			sort: false,
			scroll: false,
			ghostClass: 'rb-element-placeholder',

			// drag start
			onStart: () => {
				// angular-legacy-sortable expects `Sortable.active.lastPullMode` to be
				// defined but this property is no longer supported by the SortableJS.
				// https://github.com/SortableJS/Sortable/commit/073a57458dfd24e00826a02917cc49eb7c2f06fb
				(Sortable.active as any).lastPullMode = 'clone';

				this.isDragging = true;
				this.rubricController.setCanvasActive(true);
			},

			// drag end
			onEnd: () => {
				delete (Sortable.active as any).lastPullMode;
				this.isDragging = false;
				this.suppressNextClick();
				this.rubricController.setCanvasActive(false);
			},

			onRemove: () => {
				// This resets the template elements back to the original order.
				// After a template element is added to the canvas, angular-legacy-sortable
				// modifies the elements array and moves the item to the beginning.
				this.elements = Array.from(elements);
				this.$scope.$digest();
			}
		};
	}

	public isClickToAddEnabled (): boolean {
		return this.responsiveView.isAtMost(Sizes.SMALLER);
	}

	public onPanelItemActivate (element: any, event: MouseEvent | KeyboardEvent): void {
		if (!this.isClickToAddEnabled() || this.isDragging || this.suppressClick) {
			return;
		}

		if (event.type === 'keydown') {
			const keyboardEvent = event as KeyboardEvent;
			if (keyboardEvent.repeat || !this.isActivationKey(keyboardEvent)) {
				return;
			}
		}

		event.preventDefault();
		event.stopPropagation();

		this.rubricController.addItem(element);
		this.rubricController.dataChanged();
		this.scrollToLatestElement();
	}

	private suppressNextClick (): void {
		this.suppressClick = true;
		this.$timeout(() => {
			this.suppressClick = false;
		});
	}

	private isActivationKey (event: KeyboardEvent): boolean {
		const key = event.key;
		const keyCode = event.keyCode;
		return key === 'Enter' || key === ' ' || key === 'Spacebar' || keyCode === 13 || keyCode === 32;
	}

	private scrollToLatestElement (): void {
		this.$timeout(() => {
			const canvas = this.$document[0].querySelector('.rb-canvas.rb-edit') as HTMLElement | null;
			if (!canvas) {
				return;
			}

			const elementsList = canvas.querySelectorAll('.rb-element-draggable');
			const latestElement = elementsList[elementsList.length - 1] as HTMLElement | undefined;
			latestElement?.scrollIntoView({block: 'nearest'});
		});
	}
}
